import React, { useState } from 'react';
import './style.scoped.scss';
import { FaChevronDown } from 'react-icons/fa';
import { Content } from '../Content';
import { Policies } from '../Policies';

// Endpoint titles read "GET Documents" — split the method off into a badge.
const ItemLabel = ({ title }) => {
  const match = title.match(/^(GET|POST|PUT|PATCH|DELETE)\s+(.*)$/);
  if (!match) return <span className='item-title'>{title}</span>;
  return (
    <>
      <span className='method-badge'>{match[1]}</span>
      <span className='item-title'>{match[2]}</span>
    </>
  );
};

const NavItem = ({ item, selectedItem, handleClick }) => (
  <span
    className={`leftNav__wrapper__items__item ${
      selectedItem.title === item.title ? 'item-active' : ''
    } `}
    onClick={() => handleClick(item)}
  >
    <ItemLabel title={item.title} />
  </span>
);

// Endpoint nav grouped by the resource each endpoint operates on. Titles are
// stable (they double as playground/deep-link keys), so we map them here rather
// than threading a `group` field through every Content entry. Any endpoint not
// listed falls into an "Other" group below, so nothing silently disappears.
const ENDPOINT_GROUPS = [
  {
    label: 'Documents',
    titles: [
      'GET Documents',
      'GET Documents by Title',
      'GET Document by ID',
      'GET Sections of a Document by ID',
      'GET Related Documents by ID',
      'GET Glossary of a Document by ID',
      'GET Glossary by Title',
    ],
  },
  {
    label: 'Sections',
    titles: [
      'GET Section by ID',
      'GET Sections by Title',
      'GET Related Sections by ID',
    ],
  },
  {
    label: 'Search',
    titles: ['Full-Text Search'],
  },
  {
    label: 'Taxonomy',
    titles: [
      'GET Taxonomies',
      'GET Taxonomy Items',
      'GET Taxonomy Item by ID',
      'GET Documents for Taxonomy Item',
    ],
  },
  {
    label: 'Related Content',
    titles: ['GET Related Content by Document', 'GET Related Content by Taxonomy Item'],
  },
  {
    label: 'AI',
    titles: [
      'GET Summary by ID',
      'POST Query a Document',
      'GET My AI Queries',
      'POST Compare Documents',
      'GET Comparison Result',
    ],
  },
];

const LeftNav = ({ isDocs, handleClick, selectedItem, handleCopyClick }) => {
  const items = Content(handleCopyClick);
  const byTitle = {};
  items.forEach((it) => {
    byTitle[it.title] = it;
  });

  const guidePages = items.filter((it) => it.section === false);
  const grouped = new Set();
  ENDPOINT_GROUPS.forEach((g) => g.titles.forEach((t) => grouped.add(t)));
  const ungrouped = items.filter(
    (it) => it.section !== false && !grouped.has(it.title)
  );

  // Ordered, collapsible categories. "Guide" first, then each endpoint group,
  // then any leftovers under "Other" so nothing silently disappears.
  const groups = [
    { label: 'Guide', items: guidePages },
    ...ENDPOINT_GROUPS.map((g) => ({
      label: g.label,
      items: g.titles.map((t) => byTitle[t]).filter(Boolean),
    })),
    ...(ungrouped.length ? [{ label: 'Other', items: ungrouped }] : []),
  ].filter((g) => g.items.length);

  // Track which categories the user has expanded. Empty = all collapsed, so the
  // nav starts short and only the group holding the current page is open.
  const [expanded, setExpanded] = useState({});
  const toggle = (label) =>
    setExpanded((e) => ({ ...e, [label]: !e[label] }));

  return (
    <div className='leftNav'>
      <div className='leftNav__wrapper'>
        <div className='leftNav__wrapper__items'>
          {isDocs
            ? groups.map((group) => {
                // The group holding the current page stays open regardless, so
                // the active item is never hidden behind a collapsed header.
                const hasActive = group.items.some(
                  (i) => i.title === selectedItem.title
                );
                const isOpen = hasActive || !!expanded[group.label];
                return (
                  <React.Fragment key={group.label}>
                    <button
                      type='button'
                      className={`leftNav__wrapper__items__label${
                        isOpen ? ' is-open' : ''
                      }`}
                      onClick={() => toggle(group.label)}
                      aria-expanded={isOpen}
                    >
                      <span>{group.label}</span>
                      <FaChevronDown className='leftNav__chev' />
                    </button>
                    {isOpen &&
                      group.items.map((item, index) => (
                        <NavItem
                          key={`${group.label}-${index}`}
                          item={item}
                          selectedItem={selectedItem}
                          handleClick={handleClick}
                        />
                      ))}
                  </React.Fragment>
                );
              })
            : Policies.map((item, index) => (
                <NavItem
                  key={index}
                  item={item}
                  selectedItem={selectedItem}
                  handleClick={handleClick}
                />
              ))}
        </div>
      </div>
    </div>
  );
};

export default LeftNav;
